<script lang="ts">
  import { onMount, tick } from 'svelte';
  import type { NavigationIntent } from '../actions/navigation-intent';
  import ChapterCard from './ChapterCard.svelte';
  import type { ReaderState } from '../reader/reader-state.svelte';
  import type { ReaderStreamState } from '../reader/stream-state.svelte';
  import { STREAM_PRIME_RADIUS } from '../reader/types';
  import type {
    CrossReferenceNavigation,
    NavigationTarget,
    StreamChapterItem,
    StreamNavigationRequest,
  } from '../reader/types';

  let {
    reader,
    stream,
    streamIndex,
    active = false,
    onReference,
  }: {
    reader: ReaderState;
    stream: ReaderStreamState;
    streamIndex: number;
    active?: boolean;
    onReference?: (reference: CrossReferenceNavigation, intent: NavigationIntent) => void;
  } = $props();

  let scroller = $state.raw<HTMLElement | null>(null);
  let heights = $state.raw<number[]>([]);
  let viewportHeight = $state(0);

  let scrollRequest: number | null = null;
  let scrollNavigationId = 0;
  let lockedActiveIndex: number | null = null;
  let handledNavigationNonce = 0;
  let releaseLockOnNextScroll = false;
  let allowScrollActivation = false;
  let scrollActivationHandle: ReturnType<typeof setTimeout> | null = null;
  let boundStream = $state.raw<ReaderStreamState | null>(null);

  onMount(() => {
    syncHeights();
    queueViewportUpdate();

    return () => {
      if (scrollRequest !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(scrollRequest);
      }

      if (scrollActivationHandle !== null) {
        clearTimeout(scrollActivationHandle);
      }
    };
  });

  $effect(() => {
    stream.sequence.items.length;
    syncHeights();
    queueViewportUpdate();
  });

  $effect(() => {
    viewportHeight;
    queueViewportUpdate();
  });

  $effect(() => {
    active;
    queueViewportUpdate();
  });

  $effect(() => {
    if (boundStream === stream) {
      return;
    }

    boundStream = stream;
    handledNavigationNonce = 0;
    scrollNavigationId = 0;
    lockedActiveIndex = null;
    releaseLockOnNextScroll = false;
    allowScrollActivation = false;
    if (scrollActivationHandle !== null) {
      clearTimeout(scrollActivationHandle);
      scrollActivationHandle = null;
    }
    queueViewportUpdate();
  });

  $effect(() => {
    const request = stream.navigationRequest;
    if (!request || request.nonce === handledNavigationNonce || !scroller) {
      return;
    }

    handledNavigationNonce = request.nonce;
    lockedActiveIndex = request.target.itemIndex;
    stream.primeVisibleWindow(request.target.itemIndex, STREAM_PRIME_RADIUS);
    const navigationId = ++scrollNavigationId;
    scheduleScrollToIndex(request.target.itemIndex, request.behavior, request.target.align);
    void runSettledScroll(request, navigationId);
  });

  function handleScroll(): void {
    const nextScrollTop = scroller?.scrollTop ?? 0;
    const scrollChanged = Math.abs(nextScrollTop - stream.scrollTop) > 1;
    if (releaseLockOnNextScroll && scrollChanged) {
      releaseLockOnNextScroll = false;
      lockedActiveIndex = null;
      scrollNavigationId += 1;
      noteUserScrollInteraction();
    } else if (scrollChanged && allowScrollActivation) {
      noteUserScrollInteraction();
    }

    stream.setScrollTop(nextScrollTop);
    reader.scheduleWorkspacePersistence(500);
    queueViewportUpdate();
  }

  function armScrollRelease(): void {
    releaseLockOnNextScroll = true;
  }

  function cancelScrollRelease(): void {
    releaseLockOnNextScroll = false;
  }

  function releaseFromWheel(): void {
    releaseLockOnNextScroll = false;
    lockedActiveIndex = null;
    scrollNavigationId += 1;
    noteUserScrollInteraction();
  }

  function noteUserScrollInteraction(): void {
    allowScrollActivation = true;
    if (scrollActivationHandle !== null) {
      clearTimeout(scrollActivationHandle);
    }

    scrollActivationHandle = setTimeout(() => {
      allowScrollActivation = false;
      scrollActivationHandle = null;
    }, 220);
  }

  function queueViewportUpdate(): void {
    if (typeof requestAnimationFrame !== 'function') {
      updateViewportState();
      return;
    }

    if (scrollRequest !== null) {
      return;
    }

    scrollRequest = requestAnimationFrame(() => {
      scrollRequest = null;
      updateViewportState();
    });
  }

  function updateViewportState(): void {
    if (!scroller) return;

    const slots = Array.from(scroller.querySelectorAll<HTMLElement>('.chapter-slot'));
    if (slots.length === 0) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const panel = scroller.closest<HTMLElement>('[data-cy="reader-stream-panel"]');
    const panelHeight = panel?.clientHeight ?? 0;
    const windowHeight =
      typeof window !== 'undefined' && Number.isFinite(window.innerHeight)
        ? window.innerHeight
        : 0;
    const measuredViewportHeight = scroller.clientHeight || viewportHeight || 640;
    const boundedViewportHeight = Math.min(
      measuredViewportHeight,
      panelHeight || measuredViewportHeight,
      windowHeight || measuredViewportHeight,
    );
    const nextViewportHeight = Math.max(320, boundedViewportHeight);
    const viewportCenter = nextViewportHeight / 2;
    const rootMargin = nextViewportHeight * 2.25;
    const visibleIndices: number[] = [];
    const nextHeights = [...heights];
    let activeIndex: number | null = null;
    let activeDistance = Number.POSITIVE_INFINITY;

    for (const slot of slots) {
      const index = indexFromElement(slot);
      if (index === null) continue;

      const rect = slot.getBoundingClientRect();
      const top = rect.top - scrollerRect.top;
      const bottom = rect.bottom - scrollerRect.top;

      if (slot.offsetHeight > 0) {
        nextHeights[index] = slot.offsetHeight;
      }

      if (bottom > -rootMargin && top < nextViewportHeight + rootMargin) {
        visibleIndices.push(index);
      }

      if (bottom <= 0 || top >= nextViewportHeight) {
        continue;
      }

      const chapterCenter = top + rect.height / 2;
      const distance = Math.abs(chapterCenter - viewportCenter);
      if (distance < activeDistance) {
        activeDistance = distance;
        activeIndex = index;
      }
    }

    heights = nextHeights;

    if (!active) {
      return;
    }

    if (lockedActiveIndex !== null) {
      const anchoredVisible: number[] = [];
      const anchorRadius = STREAM_PRIME_RADIUS + 2;
      const chapterCount = stream.sequence.items.length;
      for (
        let index = Math.max(0, lockedActiveIndex - anchorRadius);
        index <= Math.min(chapterCount - 1, lockedActiveIndex + anchorRadius);
        index += 1
      ) {
        anchoredVisible.push(index);
      }

      stream.setVisible(anchoredVisible);
      return;
    }

    if (visibleIndices.length === 0 && stream.currentItemIndex !== null) {
      stream.primeVisibleWindow(stream.currentItemIndex, STREAM_PRIME_RADIUS);
      return;
    }

    const maxVisibleIndices = STREAM_PRIME_RADIUS * 3 + 3;
    let nextVisible = visibleIndices;
    if (visibleIndices.length > maxVisibleIndices) {
      const anchorIndex = lockedActiveIndex ?? activeIndex ?? stream.currentItemIndex;
      if (anchorIndex !== null) {
        nextVisible = visibleIndices.filter(
          (index) => Math.abs(index - anchorIndex) <= STREAM_PRIME_RADIUS + 2,
        );
      }
    }

    stream.setVisible(nextVisible);

    if (activeIndex === null || lockedActiveIndex !== null || !allowScrollActivation) {
      return;
    }

    reader.activateStreamFromScroll(stream.id, activeIndex);
  }

  function scheduleScrollToIndex(
    index: number,
    behavior: ScrollBehavior,
    align: NavigationTarget['align'],
    attempt = 0,
  ): void {
    if (typeof requestAnimationFrame !== 'function') return;

    requestAnimationFrame(() => {
      const slot = findSlot(index);
      if (!slot) {
        if (attempt < 4) {
          scheduleScrollToIndex(index, behavior, align, attempt + 1);
        }
        return;
      }

      scrollContainerToSlot(slot, behavior, align);
    });
  }

  async function runSettledScroll(
    request: StreamNavigationRequest,
    navigationId: number,
  ): Promise<void> {
    const dependentIndices = new Set<number>();
    for (
      let index = Math.max(0, request.target.itemIndex - 4);
      index <= request.target.itemIndex + 1;
      index += 1
    ) {
      dependentIndices.add(index);
    }

    await Promise.all(
      Array.from(dependentIndices.values()).map((index) => stream.ensureItemLoaded(index)),
    );
    await tick();
    await waitForNextFrame();
    await tick();

    if (navigationId !== scrollNavigationId || !scroller) {
      return;
    }

    if (request.restoreScrollTop !== null) {
      setScrollTop(request.restoreScrollTop, request.behavior);
      await waitForNextFrame();
      if (navigationId !== scrollNavigationId) {
        return;
      }
      lockedActiveIndex = null;
    } else if (request.target.targetVerse === null) {
      scheduleScrollToIndex(request.target.itemIndex, request.behavior, request.target.align);
    } else {
      scheduleScrollToVerse(request.target.itemIndex, request.target.targetVerse, request.behavior);
    }

    if (navigationId === scrollNavigationId) {
      queueViewportUpdate();
    }
  }

  function scheduleScrollToVerse(
    index: number,
    verse: number,
    behavior: ScrollBehavior,
    attempt = 0,
  ): void {
    if (typeof requestAnimationFrame !== 'function') return;

    requestAnimationFrame(() => {
      const verseTrigger = findVerseTrigger(index, verse);
      if (!verseTrigger) {
        if (attempt < 6) {
          scheduleScrollToVerse(index, verse, behavior, attempt + 1);
        } else {
          scheduleScrollToIndex(index, behavior, 'top');
        }
        return;
      }

      scrollContainerToElement(verseTrigger, behavior);
    });
  }

  function findSlot(index: number): HTMLElement | null {
    return scroller?.querySelector<HTMLElement>(`.chapter-slot[data-index="${index}"]`) ?? null;
  }

  function findVerseTrigger(index: number, verse: number): HTMLElement | null {
    return findSlot(index)?.querySelector<HTMLElement>(`.verse-trigger[data-verse="${verse}"]`) ?? null;
  }

  function scrollOffsetFor(element: HTMLElement, minimum = 0): number {
    return Math.max(minimum, Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0);
  }

  function scrollContainerToSlot(
    slot: HTMLElement,
    behavior: ScrollBehavior,
    align: NavigationTarget['align'],
  ): void {
    if (!scroller) return;

    const rect = slot.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const availableViewportHeight = Math.max(0, scroller.clientHeight);
    const topOffset = scrollOffsetFor(slot);
    const centerOffset =
      align === 'center' && rect.height < availableViewportHeight
        ? Math.max(0, (availableViewportHeight - rect.height) / 2)
        : topOffset;
    const top = Math.max(0, scroller.scrollTop + (rect.top - scrollerRect.top) - centerOffset);

    setScrollTop(top, behavior);
  }

  function scrollContainerToElement(element: HTMLElement, behavior: ScrollBehavior): void {
    if (!scroller) return;

    const rect = element.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const topOffset = scrollOffsetFor(element, 10);
    const top = Math.max(0, scroller.scrollTop + (rect.top - scrollerRect.top) - topOffset);

    setScrollTop(top, behavior);
  }

  function indexFromElement(target: Element): number | null {
    const rawIndex = (target as HTMLElement).dataset['index'];
    if (rawIndex === undefined) return null;

    const index = Number(rawIndex);
    return Number.isNaN(index) ? null : index;
  }

  function syncHeights(): void {
    const nextLength = stream.sequence.items.length;
    if (nextLength === heights.length) {
      return;
    }

    heights = Array.from({ length: nextLength }, (_, index) => heights[index] ?? 0);
  }

  function waitForNextFrame(): Promise<void> {
    if (typeof requestAnimationFrame !== 'function') {
      return Promise.resolve();
    }

    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  function setScrollTop(top: number, behavior: ScrollBehavior): void {
    if (!scroller) return;

    if (typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({ top, behavior });
    } else {
      scroller.scrollTop = top;
    }

    stream.setScrollTop(top);
    reader.scheduleWorkspacePersistence(500);
  }

  function completionControlFor(item: StreamChapterItem): {
    label: string;
    checked: boolean;
    onToggle: () => void;
  } | null {
    if (!reader.isHornerStream(stream)) {
      return null;
    }

    return {
      label: 'Mark Read',
      checked: reader.isHornerChapterRead(item.bookId, item.chapter),
      onToggle: () => reader.toggleHornerChapterRead(stream.id, item.bookId, item.chapter),
    };
  }
</script>

<svelte:window onresize={queueViewportUpdate} />

<div
  bind:this={scroller}
  bind:clientHeight={viewportHeight}
  class="chapter-stream"
  data-cy="chapter-stream"
  data-stream-active={active ? 'true' : 'false'}
  data-stream-id={stream.id}
  data-stream-index={streamIndex}
  role="region"
  aria-label={`Chapter stream ${streamIndex + 1}`}
  onscroll={handleScroll}
  onpointerdown={armScrollRelease}
  onpointerup={cancelScrollRelease}
  onpointercancel={cancelScrollRelease}
  onpointerleave={cancelScrollRelease}
  onwheel={releaseFromWheel}
>
  {#each stream.sequence.items as item, i (item.key)}
    {@const ref = reader.chapterRefAt(item.catalogIndex)}
    {@const chapter = reader.chapterAt(item.catalogIndex)}
    <section
      class="chapter-slot"
      data-index={i}
      data-book={ref?.bookName}
      data-chapter={ref?.chapter}
      data-sequence-kind={stream.sequence.kind}
      style={`min-height: ${stream.visible.has(i) ? 0 : reader.slotHeight(item.catalogIndex, heights[i])}px`}
    >
      {#if ref && stream.visible.has(i)}
        {#if chapter}
          <div class="chapter-slot__frame">
            <ChapterCard
              bookName={chapter.bookName}
              chapter={chapter.chapter}
              verses={chapter.verses}
              testament={chapter.testament}
              highlightedVerse={stream.highlightedVerseForChapter(chapter)}
              highlightedVerseNonce={stream.highlightNonceForChapter(chapter)}
              completionControl={completionControlFor(item)}
              onReference={onReference}
            />
          </div>
        {:else}
          <article class="chapter-placeholder" aria-live="polite" aria-busy="true">
            <p>Loading {ref.bookName} {ref.chapter}…</p>
          </article>
        {/if}
      {/if}
    </section>
  {/each}
</div>
